import { useMutation, useQuery } from '@apollo/client';
import { defaultTagColor, tagPalette } from '@meshhub/design-tokens/tokens';
import { Button, TBody, TD, TH, THead, TR, Table, TagChip } from '@meshhub/design-tokens/ui';
import { Pencil, Plus } from 'lucide-react';
import React, { useState } from 'react';
import {
  ActiveBadge,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  CheckboxField,
  SettingsPage,
  ShowInactiveToggle,
  TextField,
} from '../../components/settings/SettingsKit.web.jsx';
import {
  ADMIN_TAGS_QUERY,
  CREATE_TAG,
  SET_TAG_ACTIVE,
  UPDATE_TAG,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

export function TagsScreen() {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const variables = { includeInactive };
  const { data, loading } = useQuery(ADMIN_TAGS_QUERY, { variables });
  const refetch = [{ query: ADMIN_TAGS_QUERY, variables }];

  const [createTag, createState] = useMutation(CREATE_TAG, { refetchQueries: refetch });
  const [updateTag, updateState] = useMutation(UPDATE_TAG, { refetchQueries: refetch });
  const [setActive, activeState] = useMutation(SET_TAG_ACTIVE, { refetchQueries: refetch });

  const rows = data?.tags ?? [];

  return (
    <SettingsPage
      title={S.settings.sections.tags}
      hint={S.settings.common.noDelete}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={setIncludeInactive} />
          <Button variant="primary" onClick={() => setEditing('new')} className="gap-2">
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <MutationError error={createState.error || updateState.error || activeState.error} />
      <LoadingOrEmpty loading={loading && rows.length === 0} empty={!loading && rows.length === 0} />

      {rows.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>{S.settings.tags.name}</TH>
              <TH>{S.settings.tags.color}</TH>
              <TH>{S.settings.tags.preview}</TH>
              <TH>{S.settings.tags.isAssessment}</TH>
              <TH>{S.settings.common.status}</TH>
              <TH>{S.settings.common.actions}</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((tag) => (
              <TR key={tag.id} className={tag.isActive ? '' : 'opacity-60'}>
                <TD className="font-medium">{tag.name}</TD>
                <TD>
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-4 w-4 rounded border border-border"
                      style={{ backgroundColor: tag.colorHex }}
                    />
                    <span dir="ltr" className="text-text-muted">
                      {tag.colorHex}
                    </span>
                  </span>
                </TD>
                <TD>
                  <TagChip name={tag.name} colorHex={tag.colorHex} />
                </TD>
                <TD className="text-text-muted">
                  {tag.isAssessment ? S.app.yes : S.app.no}
                </TD>
                <TD>
                  <ActiveBadge isActive={tag.isActive} />
                </TD>
                <TD>
                  <span className="flex items-center gap-1">
                    <Button variant="ghost" title={S.app.edit} onClick={() => setEditing(tag)}>
                      <Pencil size={14} aria-hidden="true" />
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirming(tag)}>
                      {tag.isActive ? S.app.deactivate : S.app.activate}
                    </Button>
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {editing && (
        <TagDialog
          tag={editing === 'new' ? null : editing}
          busy={createState.loading || updateState.loading}
          onClose={() => setEditing(null)}
          onSubmit={async ({ name, colorHex, isAssessment }) => {
            const variables = { name, colorHex, isAssessment };
            if (editing === 'new') await createTag({ variables });
            else await updateTag({ variables: { id: editing.id, ...variables } });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? S.settings.common.confirmDeactivate(confirming?.name ?? '')
            : S.settings.common.confirmActivate(confirming?.name ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() =>
          setActive({ variables: { id: confirming.id, isActive: !confirming.isActive } })
        }
      />
    </SettingsPage>
  );
}

function TagDialog({ tag, onClose, onSubmit, busy }) {
  const [name, setName] = useState(tag?.name ?? '');
  const [colorHex, setColorHex] = useState(tag?.colorHex ?? defaultTagColor);
  const [isAssessment, setIsAssessment] = useState(Boolean(tag?.isAssessment));

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={tag ? `${S.app.edit} — ${tag.name}` : S.settings.tags.createTitle}
      busy={busy}
      onSubmit={() => onSubmit({ name, colorHex, isAssessment })}
    >
      <TextField
        label={S.settings.tags.name}
        value={name}
        required
        onChange={(event) => setName(event.target.value)}
      />

      <div className="flex items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-text-muted">{S.settings.tags.color}</span>
          <input
            type="color"
            value={colorHex}
            onChange={(event) => setColorHex(event.target.value)}
            className="h-control w-16 cursor-pointer rounded border border-border bg-bg"
            aria-label={S.settings.tags.color}
          />
        </label>
        <TextField
          label=""
          value={colorHex}
          dir="ltr"
          className="flex-1"
          onChange={(event) => setColorHex(event.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {tagPalette.map((swatch) => (
          <button
            key={swatch.name}
            type="button"
            aria-label={swatch.name}
            onClick={() => setColorHex(swatch.value)}
            className={`h-5 w-5 rounded-full border ${
              colorHex.toLowerCase() === swatch.value ? 'border-text' : 'border-border'
            }`}
            style={{ backgroundColor: swatch.value }}
          />
        ))}
      </div>

      {/* Which tags are «ارزیابی» has to be settable, or the four seeded
          rows would be the only ones there can ever be — the same lesson as a
          vocabulary the admin surface cannot reach (CLAUDE.md §15). An adopter
          with their own words ticks this on their own tags. */}
      <CheckboxField
        label={S.settings.tags.isAssessment}
        checked={isAssessment}
        onChange={(event) => setIsAssessment(event.target.checked)}
      />
      <p className="text-xs text-text-muted">{S.settings.tags.isAssessmentHint}</p>

      <div className="flex items-center gap-3">
        <span className="text-sm text-text-muted">{S.settings.tags.preview}</span>
        <TagChip name={name || S.settings.tags.name} colorHex={colorHex} />
      </div>
    </FormDialog>
  );
}
